'use client';

import {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type KeyboardEvent,
} from 'react';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  Text,
  Input,
  IconButton,
  Tooltip,
  Badge,
  LegacyDialog,
} from '@terra/ui-design-system';
import {
  AlertCircleIcon,
  Bookmark01Icon,
  SourceCodeIcon,
  CheckmarkCircle01Icon,
  ArrowDown01Icon,
  HashtagIcon,
  HelpCircleIcon,
  WorkHistoryIcon,
  Search01Icon,
  TextIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import {
  useFilterPresetsStore,
  useAllPresets,
  useRecentQueries,
} from '../../store/filterPresetsStore';
import {
  getTqlSuggestions,
  getTqlDocumentation,
  validateTql,
  tokenizeForHighlight,
  type TqlSuggestion,
  type HighlightToken,
} from '../../utils/tql';

// Type for recent query suggestions (discriminated union with TqlSuggestion)
type RecentQuerySuggestion = {
  type: 'recent';
  label: string;
  query: string;
};
// Combined suggestion type with discriminated union
type CombinedSuggestion = TqlSuggestion | RecentQuerySuggestion;

// Type guard to check if suggestion is a recent query
function isRecentSuggestion(
  suggestion: CombinedSuggestion,
): suggestion is RecentQuerySuggestion {
  return suggestion.type === 'recent';
}

// Type guard to check if suggestion is an TQL suggestion
function isTqlSuggestion(
  suggestion: CombinedSuggestion,
): suggestion is TqlSuggestion {
  return suggestion.type !== 'recent';
}

// Get syntax highlighting color for token type
function getTokenColor(type: HighlightToken['type']): string {
  switch (type) {
    case 'namespace':
    case 'field':
      return token('colors.sky.11');
    case 'operator':
      return token('colors.yellow.11');
    case 'value':
      return token('colors.green.11');
    case 'keyword':
      return token('colors.purple.11');
    case 'preset':
      return token('colors.crimson.11');
    case 'paren':
      return token('colors.fg.muted');
    case 'error':
      return token('colors.tomato.11');
    case 'text':
    default:
      return token('colors.fg.default');
  }
}

type TqlFilterInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onApply?: (query: string) => void;
  disabled?: boolean;
  compact?: boolean;
};

export function TqlFilterInput({
  value,
  onChange,
  placeholder = 'Filter requests... (e.g., req.path.cont("/api"))',
  onApply,
  disabled = false,
  compact = false,
}: TqlFilterInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const presets = useAllPresets();
  const recentQueries = useRecentQueries();
  const addToRecentQueries = useFilterPresetsStore(
    (state) => state.addToRecentQueries,
  );
  // Validate the current query
  const validation = useMemo(() => {
    if (!value.trim()) {
      return { valid: true };
    }

    return validateTql(value);
  }, [value]);
  // Tokenize for syntax highlighting
  const highlightTokens = useMemo(() => {
    if (!value) return [];

    return tokenizeForHighlight(value);
  }, [value]);
  // Get suggestions based on current input and cursor position
  const suggestions = useMemo(() => {
    if (!isFocused || !showSuggestions) return [];

    return getTqlSuggestions(value, cursorPosition, {
      presets,
      recentValues: {
        methods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS', 'HEAD'],
      },
    });
  }, [value, cursorPosition, isFocused, showSuggestions, presets]);
  // Get recent queries that don't match current input
  const filteredRecentQueries = useMemo(() => {
    if (value.trim()) return [];

    return recentQueries.slice(0, 5);
  }, [recentQueries, value]);
  // Combined suggestions list
  const allSuggestions = useMemo((): CombinedSuggestion[] => {
    const result: CombinedSuggestion[] = [];

    // Add recent queries when input is empty
    if (!value.trim() && filteredRecentQueries.length > 0) {
      for (const recentQuery of filteredRecentQueries) {
        result.push({ type: 'recent', label: recentQuery, query: recentQuery });
      }
    }

    // Add regular suggestions
    result.push(...suggestions);

    return result;
  }, [suggestions, filteredRecentQueries, value]);
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
      setCursorPosition(e.target.selectionStart || 0);
      setShowSuggestions(true);
      setSelectedSuggestionIndex(-1);
    },
    [onChange],
  );
  const handleInputFocus = useCallback(() => {
    // Cancel any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    setIsFocused(true);
    setShowSuggestions(true);
  }, []);
  const handleInputBlur = useCallback(() => {
    // Delay hiding suggestions to allow click events on buttons
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
      setShowSuggestions(false);
    }, 200);
  }, []);

  // Cleanup blur timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);
  const applySuggestion = useCallback(
    (suggestion: TqlSuggestion) => {
      const input = inputRef.current;

      if (!input) return;
      const beforeCursor = value.substring(0, cursorPosition);
      let tokenStart: number;
      let tokenEnd: number = cursorPosition;
      let adjustedInsertText = suggestion.insertText;

      if (suggestion.type === 'value') {
        // For values, find the start of the value (after opening quote or paren)
        const lastQuoteIndex = beforeCursor.lastIndexOf('"');
        const lastParenIndex = beforeCursor.lastIndexOf('(');

        if (lastQuoteIndex > lastParenIndex) {
          // Inside quotes - replace from after the quote
          // e.g., `req.method.eq("PO` -> tokenStart after the quote
          tokenStart = lastQuoteIndex + 1;
          // Strip the leading quote from insertText since we're already inside quotes
          // insertText: `"GET")` -> `GET")`
          if (adjustedInsertText.startsWith('"')) {
            adjustedInsertText = adjustedInsertText.substring(1);
          }
        } else if (lastParenIndex !== -1) {
          // After open paren but before quote (numeric value or about to type quote)
          tokenStart = lastParenIndex + 1;
        } else {
          tokenStart = cursorPosition;
        }

        // Also consume any existing closing content after cursor
        const afterCursor = value.substring(cursorPosition);
        const closingMatch = afterCursor.match(/^[^")\s]*[")]*/);

        if (closingMatch) {
          tokenEnd = cursorPosition + closingMatch[0].length;
        }
      } else if (suggestion.type === 'operator') {
        // For operators, only replace text after the last dot
        const lastDotIndex = beforeCursor.lastIndexOf('.');

        if (lastDotIndex !== -1) {
          tokenStart = lastDotIndex + 1;
        } else {
          const tokenMatch = beforeCursor.match(/[\w.-]*$/);

          tokenStart = tokenMatch
            ? cursorPosition - tokenMatch[0].length
            : cursorPosition;
        }
      } else {
        // For fields, namespaces, keywords - replace the full token
        const tokenMatch = beforeCursor.match(/[\w.-]*$/);

        tokenStart = tokenMatch
          ? cursorPosition - tokenMatch[0].length
          : cursorPosition;
      }

      // Use setRangeText to preserve undo history
      input.focus();
      input.setSelectionRange(tokenStart, tokenEnd);
      // Use execCommand for undo support, fallback to setRangeText
      const insertText = adjustedInsertText;

      if (document.queryCommandSupported?.('insertText')) {
        document.execCommand('insertText', false, insertText);
      } else {
        input.setRangeText(insertText, tokenStart, tokenEnd, 'end');
        // Manually trigger onChange since setRangeText doesn't fire input event
        onChange(input.value);
      }

      // Keep suggestions open if the inserted text ends with a dot or open paren
      const keepSuggestionsOpen =
        insertText.endsWith('.') || insertText.endsWith('(');

      if (!keepSuggestionsOpen) {
        setShowSuggestions(false);
      } else {
        // Ensure suggestions stay open
        setIsFocused(true);
        setShowSuggestions(true);
      }

      const newCursorPos = tokenStart + insertText.length;

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          inputRef.current.focus();
          setCursorPosition(newCursorPos);
          if (keepSuggestionsOpen) {
            setIsFocused(true);
            setShowSuggestions(true);
          }
        }
      }, 0);
    },
    [value, cursorPosition, onChange],
  );
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions || allSuggestions.length === 0) {
        if (e.key === 'Enter' && onApply && validation.valid) {
          onApply(value);
          addToRecentQueries(value);
        }

        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev < allSuggestions.length - 1 ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev <= 0 ? allSuggestions.length - 1 : prev - 1,
          );
          break;
        case 'Enter':
        case 'Tab': {
          if (selectedSuggestionIndex >= 0) {
            e.preventDefault();
            const selected = allSuggestions[selectedSuggestionIndex];

            if (selected) {
              if (isRecentSuggestion(selected)) {
                onChange(selected.query);
              } else {
                applySuggestion(selected);
              }
            }
          } else {
            // No suggestion selected - just close dropdown
            setShowSuggestions(false);
            // Don't prevent default for Enter - allow form submission or onApply
            if (e.key === 'Enter' && onApply && validation.valid) {
              onApply(value);
              addToRecentQueries(value);
            }
          }

          break;
        }

        case 'Escape':
          setShowSuggestions(false);
          break;
      }
    },
    [
      showSuggestions,
      allSuggestions,
      selectedSuggestionIndex,
      value,
      onApply,
      validation.valid,
      addToRecentQueries,
      onChange,
      applySuggestion,
    ],
  );
  const handleClear = useCallback(() => {
    // Cancel any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    onChange('');
    inputRef.current?.focus();
    setIsFocused(true);
    setShowSuggestions(true);
  }, [onChange]);
  const handleSuggestionClick = useCallback(
    (suggestion: CombinedSuggestion) => {
      // Cancel any pending blur timeout since we're clicking in the dropdown
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }

      if (isRecentSuggestion(suggestion)) {
        onChange(suggestion.query);
        inputRef.current?.focus();
      } else {
        applySuggestion(suggestion);
      }
    },
    [onChange, applySuggestion],
  );
  // Sync scroll between input and highlight overlay
  const syncScroll = useCallback(() => {
    if (inputRef.current && highlightRef.current) {
      highlightRef.current.scrollLeft = inputRef.current.scrollLeft;
    }
  }, []);

  // Reset selected suggestion when suggestions change
  useEffect(() => {
    setSelectedSuggestionIndex(-1);
  }, [allSuggestions.length, value]);
  // Update cursor position on selection change
  useEffect(() => {
    const input = inputRef.current;

    if (!input) return;
    const handleSelectionChange = () => {
      setCursorPosition(input.selectionStart || 0);
    };

    // Sync value when native input events occur (e.g., from execCommand)
    const handleNativeInput = (e: Event) => {
      const target = e.target as HTMLInputElement;

      if (target.value !== value) {
        onChange(target.value);
      }
    };

    input.addEventListener('select', handleSelectionChange);
    input.addEventListener('click', handleSelectionChange);
    input.addEventListener('scroll', syncScroll);
    input.addEventListener('input', handleNativeInput);

    return () => {
      input.removeEventListener('select', handleSelectionChange);
      input.removeEventListener('click', handleSelectionChange);
      input.removeEventListener('scroll', syncScroll);
      input.removeEventListener('input', handleNativeInput);
    };
  }, [syncScroll, value, onChange]);
  const getSuggestionIcon = (type: string) => {
    switch (type) {
      case 'field':
        return <SourceCodeIcon size={12} />;
      case 'operator':
        return <ArrowDown01Icon size={12} />;
      case 'value':
        return <TextIcon size={12} />;
      case 'preset':
        return <Bookmark01Icon size={12} />;
      case 'recent':
        return <WorkHistoryIcon size={12} />;
      case 'keyword':
        return <HashtagIcon size={12} />;
      default:
        return <Search01Icon size={12} />;
    }
  };

  const inputHeight = compact ? '7' : '8';
  const fontSize = compact ? 'xs' : 'sm';

  return (
    <Box position="relative" w="full">
      <HStack gap="1" position="relative">
        <Box position="relative" flex="1">
          {/* Search icon */}
          <Box
            position="absolute"
            left="2"
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
            color="fg.muted"
            zIndex={2}
          >
            <Search01Icon size={compact ? 12 : 14} />
          </Box>

          {/* Syntax highlighting overlay */}
          <Box
            ref={highlightRef}
            position="absolute"
            left="7"
            top="50%"
            transform="translateY(-50%)"
            right={value ? '16' : '7'}
            pointerEvents="none"
            fontFamily="mono"
            fontSize={fontSize}
            whiteSpace="pre"
            overflow="hidden"
            zIndex={1}
          >
            {highlightTokens.map((highlightToken, idx) => (
              <Box
                as="span"
                key={idx}
                style={{ color: getTokenColor(highlightToken.type) }}
              >
                {highlightToken.text}
              </Box>
            ))}
          </Box>

          <Input
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            h={inputHeight}
            fontSize={fontSize}
            pl="7"
            pr={value ? '16' : '7'}
            fontFamily="mono"
            borderColor={
              value && !validation.valid ? 'border.error' : undefined
            }
            _focus={{
              borderColor:
                value && !validation.valid ? 'border.error' : undefined,
            }}
            _placeholder={{
              color: 'fg.subtle',
            }}
            style={{
              color: value ? 'transparent' : undefined,
              caretColor: token('colors.fg.default'),
            }}
          />

          {/* Right side indicators */}
          <HStack
            position="absolute"
            right="2"
            top="50%"
            transform="translateY(-50%)"
            gap="1"
            zIndex={2}
          >
            {/* Validation indicator */}
            {value && (
              <Tooltip
                title={validation.valid ? 'Query is valid' : validation.error}
              >
                <Box color={validation.valid ? 'fg.muted' : 'fg.error'}>
                  {validation.valid ? (
                    <CheckmarkCircle01Icon size={12} />
                  ) : (
                    <AlertCircleIcon size={12} />
                  )}
                </Box>
              </Tooltip>
            )}

            {/* Clear button */}
            {value && (
              <Box
                as="button"
                aria-label="Clear filter"
                onClick={handleClear}
                cursor="pointer"
                color="fg.muted"
                _hover={{ color: 'fg.default' }}
                display="flex"
                alignItems="center"
              >
                <Cancel01Icon size={14} />
              </Box>
            )}

            {/* Help button */}
            <Tooltip title="TQL syntax help">
              <Box
                as="button"
                aria-label="Filter help"
                onClick={() => setShowHelp(true)}
                cursor="pointer"
                color="fg.muted"
                _hover={{ color: 'fg.default' }}
                display="flex"
                alignItems="center"
              >
                <HelpCircleIcon size={14} />
              </Box>
            </Tooltip>
          </HStack>
        </Box>
      </HStack>

      {/* Suggestions dropdown */}
      {showSuggestions && allSuggestions.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left="0"
          right="0"
          mt="1"
          bg="bg.default"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          boxShadow="lg"
          maxH="300px"
          overflowY="auto"
          zIndex={100}
        >
          <VStack gap="0" alignItems="stretch">
            {/* Recent queries section */}
            {filteredRecentQueries.length > 0 && !value.trim() && (
              <>
                <Box px="3" py="1" bg="bg.subtle">
                  <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                    Recent
                  </Text>
                </Box>
                {allSuggestions
                  .filter(isRecentSuggestion)
                  .map((suggestion, suggestionIndex) => (
                    <SuggestionItem
                      key={`recent-${suggestionIndex}`}
                      suggestion={suggestion}
                      isSelected={selectedSuggestionIndex === suggestionIndex}
                      onClick={() => handleSuggestionClick(suggestion)}
                      icon={getSuggestionIcon(suggestion.type)}
                    />
                  ))}
              </>
            )}

            {/* Regular suggestions */}
            {suggestions.length > 0 && (
              <>
                {filteredRecentQueries.length > 0 && !value.trim() && (
                  <Box px="3" py="1" bg="bg.subtle">
                    <Text fontSize="xs" color="fg.muted" fontWeight="medium">
                      Suggestions
                    </Text>
                  </Box>
                )}
                {allSuggestions
                  .filter(isTqlSuggestion)
                  .map((suggestion, suggestionIndex) => {
                    const actualIndex =
                      filteredRecentQueries.length > 0 && !value.trim()
                        ? suggestionIndex + filteredRecentQueries.length
                        : suggestionIndex;

                    return (
                      <SuggestionItem
                        key={`suggestion-${suggestionIndex}`}
                        suggestion={suggestion}
                        isSelected={selectedSuggestionIndex === actualIndex}
                        onClick={() => handleSuggestionClick(suggestion)}
                        icon={getSuggestionIcon(suggestion.type)}
                      />
                    );
                  })}
              </>
            )}
          </VStack>
        </Box>
      )}

      {/* Help Dialog */}
      <LegacyDialog.Root
        open={showHelp}
        onOpenChange={({ open }) => setShowHelp(open)}
        lazyMount
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="2xl" maxH="80vh">
            <VStack gap="4" p="6" alignItems="stretch">
              <HStack justifyContent="space-between" alignItems="center">
                <LegacyDialog.Title>TQL Syntax Reference</LegacyDialog.Title>
                <LegacyDialog.CloseTrigger asChild>
                  <IconButton aria-label="Close" variant="ghost" size="sm">
                    <Cancel01Icon size={16} />
                  </IconButton>
                </LegacyDialog.CloseTrigger>
              </HStack>
              <Box
                overflowY="auto"
                maxH="60vh"
                p="4"
                bg="bg.subtle"
                borderRadius="md"
                fontFamily="mono"
                fontSize="xs"
                whiteSpace="pre-wrap"
              >
                {getTqlDocumentation()}
              </Box>
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </Box>
  );
}

type SuggestionItemProps = {
  suggestion: CombinedSuggestion;
  isSelected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
};

function SuggestionItem({
  suggestion,
  isSelected,
  onClick,
  icon,
}: SuggestionItemProps) {
  // Use type guard to determine suggestion type
  const suggestionIsRecent = isRecentSuggestion(suggestion);
  // Get appropriate badge variant based on type
  const getBadgeVariant = (): 'outline' | 'fill' => {
    switch (suggestion.type) {
      case 'field':
      case 'keyword':
      case 'recent':
        return 'outline';
      case 'operator':
      case 'value':
      case 'preset':
        return 'fill';
      default:
        return 'outline';
    }
  };

  // Extract optional properties only available on TqlSuggestion
  const detail = isTqlSuggestion(suggestion) ? suggestion.detail : undefined;
  const description = isTqlSuggestion(suggestion)
    ? suggestion.description
    : undefined;

  return (
    <HStack
      px="3"
      py="2"
      gap="2"
      cursor="pointer"
      bg={isSelected ? 'bg.muted' : 'transparent'}
      _hover={{ bg: isSelected ? 'bg.muted' : 'bg.hover' }}
      onClick={onClick}
    >
      <Box color="fg.muted" flexShrink={0}>
        {icon}
      </Box>

      <VStack gap="0" alignItems="flex-start" flex="1" minW="0">
        <HStack gap="2" w="full">
          <Text
            fontSize="xs"
            fontFamily="mono"
            color="fg.default"
            truncate
            flex="1"
          >
            {suggestion.label}
          </Text>
          {!suggestionIsRecent && detail && (
            <Text fontSize="xs" color="fg.subtle" flexShrink={0}>
              {detail}
            </Text>
          )}
        </HStack>
        {!suggestionIsRecent && description && (
          <Text fontSize="xs" color="fg.muted" truncate w="full">
            {description}
          </Text>
        )}
      </VStack>

      {/* Type badge */}
      <Badge size="sm" variant={getBadgeVariant()}>
        {suggestion.type}
      </Badge>
    </HStack>
  );
}
